import React, { FC, memo, ReactElement, useEffect, useState } from "react";
import styles from "./search.module.scss";
import { StatusText, Table, TableBody, TableHead } from "../index";
import {
  ACTIVE_TICKETS_STATUS_NAMES,
  GENDER,
  SEARCH_HEAD,
  SEARCH_HEAD_DC_DP,
} from "../../services/constants";
import { useAppDispatch, useAppSelector } from "../../core/hooks";
import { setDate } from "../../services/utils";
import {
  getCustomerSentrySingleRequests,
  getErSingleRequest,
} from "../../modules/requests/er-thunk";
import { getSingleRequest } from "../../modules/requests/insurance-thunk";
import { toggleMainPanel } from "../../app/app-slice";

interface Props {
  tooltipToggled: boolean;
}

const Search: FC<Props> = (props) => {
  const [activeTab, setActiveTab] = useState("pending");
  const { searchData } = useAppSelector((state) => state.requests);
  const { user } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const isActiveTab: any = sessionStorage.getItem("tab");

  useEffect(() => {
    setActiveTab("pending");
  }, [props.tooltipToggled]);

  const requestFunction =
    user?.organization?.type === 3 ? getSingleRequest : getErSingleRequest;

  // ✅ One boolean to drive both: header choice + row shape (hide Age・Sex column)
  const isRestrictedView =
    !!user &&
    (user?.organization?.type === 4 ||
      ((user?.role === 3 || user?.role === 2) &&
        user?.organization?.type === 2 &&
        +isActiveTab === 2));

  const HEAD = isRestrictedView ? SEARCH_HEAD_DC_DP : SEARCH_HEAD;

  let pendingUids: Array<string> = [];
  let pendingSentryGroupUuid: Array<string> = [];
  let activeUids: Array<string> = [];
  let activeSentryGroupUuid: Array<string> = [];

  const buildPendingRow = (el: any): Array<any> => {
    pendingUids.push(el.uuid);
    // @ts-ignore
    pendingSentryGroupUuid.push(el?.sentry_group_uuid);

    const requestReason = el.complaints;
    const sex = GENDER.find((d) => d.key === el.sex);

    let offersCount = 0;
    if (Array.isArray(el.pool)) {
      const offers = el.pool?.filter((d: any) => d.status === 2);
      for (const offer of offers) offersCount += offer.request_brigade.length;
    } else {
      offersCount = el.pool?.request_brigade?.length || 0;
    }

    const setStatusText = (status: number, count: number): ReactElement => {
      if (count > 0 && status !== 3) {
        return <StatusText color="gray" text={`${count} предложения`} />;
      } else if (status === 1) {
        return <StatusText color="gray" text="Новая заявка" />;
      }
      return <StatusText color="blue" text="Ожидает подтверждения " />;
    };

    // Build row matching HEAD shape
    const row: Array<any> = [
      el.request_number,
      el.address,
      requestReason,
    ];

    if (!isRestrictedView) {
      row.push(`${el.age} ・ ${sex?.type}`);
    }

    row.push(
      setDate(el.created_at, "DD/MM/YYYY"),
      `${el.name}`,
      setStatusText(el.status, offersCount)
    );

    return row;
  };

  const buildActiveRow = (el: any): Array<any> => {
    activeUids.push(el.uuid);
    // @ts-ignore
    activeSentryGroupUuid.push(el?.sentry_group_uuid);

    const requestReason = el.complaints;
    const sex = GENDER.find((d) => d.key === el.sex);

    const row: Array<any> = [
      el.request_number,
      el.address,
      requestReason,
    ];

    if (!isRestrictedView) {
      row.push(`${el.age} ・ ${sex?.type}`);
    }

    row.push(
      setDate(el.created_at, "DD/MM/YYYY"),
      `${el.name}`,
      "responsible",
      ACTIVE_TICKETS_STATUS_NAMES[el.er_call.status - 1]
    );

    return row;
  };

  const pendingList = searchData.applications.map(buildPendingRow);
  const activeList = searchData.er_calls.map(buildActiveRow);

  return (
    <div className={styles.search}>
      <div className={styles.tabs}>
        <p
          className={`${activeTab === "pending" ? styles.active : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          Заявки и предложения{" "}
          <span className={styles.caseCount}>
            {searchData.applications.length}
          </span>
        </p>
        <p
          className={`${activeTab === "active" ? styles.active : ""}`}
          onClick={() => setActiveTab("active")}
        >
          В Исполнении{" "}
          <span className={styles.caseCount}>{searchData.er_calls.length}</span>
        </p>
      </div>

      <Table className={styles.table}>
        <TableHead className={styles.tableHead}>
          {HEAD.map((el: string, i: number) => (
            <div className={`${styles.item} item`} key={i}>
              <span>{el}</span>
            </div>
          ))}
        </TableHead>

        <TableBody className="no-top-border">
          {activeTab === "pending"
            ? pendingList.map((data, i) => (
              <div
                className="row"
                key={i}
                onClick={() => {
                  if (user && user.organization.type === 4) {
                    dispatch(
                      getCustomerSentrySingleRequests({
                        uuid: pendingUids[i],
                        sentry_group_uuid: pendingSentryGroupUuid[i],
                      })
                    );
                  } else if (
                    user &&
                    (user?.role === 3 || user?.role === 2) &&
                    user?.organization?.type === 2 &&
                    +isActiveTab === 2
                  ) {
                    dispatch(
                      getCustomerSentrySingleRequests({
                        uuid: pendingUids[i],
                        sentry_group_uuid: pendingSentryGroupUuid[i],
                      })
                    );
                    dispatch(toggleMainPanel());
                  } else {
                    dispatch(requestFunction(pendingUids[i]));
                  }
                }}
              >
                {data.map((cell, j) => (
                  <div key={j} className={`${styles.item} item`}>
                      <span title={typeof cell === "string" ? cell : undefined}>
                        {cell}
                      </span>
                  </div>
                ))}
              </div>
            ))
            : activeList.map((data, i) => (
              <div
                className="row"
                key={i}
                onClick={() => {
                  if (user && user.organization.type === 4) {
                    dispatch(
                      getCustomerSentrySingleRequests({
                        uuid: activeUids[i],
                        sentry_group_uuid: activeSentryGroupUuid[i],
                      })
                    );
                  } else if (
                    user &&
                    (user?.role === 3 || user?.role === 2) &&
                    user?.organization?.type === 2 &&
                    +isActiveTab === 2
                  ) {
                    dispatch(
                      getCustomerSentrySingleRequests({
                        uuid: activeUids[i],
                        sentry_group_uuid: activeSentryGroupUuid[i],
                      })
                    );
                    dispatch(toggleMainPanel());
                  } else {
                    dispatch(requestFunction(activeUids[i]));
                  }
                }}
              >
                {data.map((cell, j) => (
                  <div key={j} className={`${styles.item} item`}>
                      <span title={typeof cell === "string" ? cell : undefined}>
                        {cell}
                      </span>
                  </div>
                ))}
              </div>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default memo(Search);
